import {createRouter, createWebHistory} from 'vue-router';
import {computed} from 'vue';
import notifier from '@/core/utils/notifier.js';
import HomePage from '@/modules/home/views/HomePage.vue';
import DefaultLayout from '@/core/views/DefaultLayout.vue';
import {useAuthStore} from "@/store/authStore.js";
import {setDocumentTitle} from "@/utils/title.js";
import {verifyJWT} from "@/core/utils/jwt";
import {ROLE_ADMIN} from "@/constants.js";
import {ROUTES} from "@/router/routes.js";
import {setRouter} from "@/router/navigation.js";

// Экспортируем ROUTES для обратной совместимости
export {ROUTES};

const routes = [
    {
        path: ROUTES.MAINTENANCE.path,
        name: ROUTES.MAINTENANCE.name,
        component: () => import('@/modules/maintenance/views/MaintenancePage.vue'),
    },
    {
        path: '/',
        component: DefaultLayout,
        children: [
            {
                path: ROUTES.HOME.path,
                name: ROUTES.HOME.name,
                component: HomePage,
                meta: {title: 'Главная - Буйян'}
            },
            {
                path: ROUTES.SEARCH.path,
                name: ROUTES.SEARCH.name,
                component: () => import('@/modules/search/views/SearchResultsPage.vue'),
                meta: {title: 'Поиск - Буйян'}
            },
            {
                path: ROUTES.MODERATION.path,
                name: ROUTES.MODERATION.name,
                component: () => import('@/modules/moderation/views/ModerationPage.vue'),
                meta: {
                    title: 'Модерация - Буйян',
                    permissions: ['moderate_vacancy'],
                }
            },
            {
                path: ROUTES.REGISTRATION.path,
                name: ROUTES.REGISTRATION.name,
                component: () => import('@/modules/registration/view/RegistrationPage.vue'),
                meta: {
                    title: 'Регистрация - Буйян',
                }
            },
            {
                path: ROUTES.LOGIN.path,
                name: ROUTES.LOGIN.name,
                component: () => import('@/modules/login/view/LoginPage.vue'),
                meta: {
                    title: 'Вход - Буйян',
                }
            },
            {
                path: ROUTES.RESTORE_PASSWORD.path,
                name: ROUTES.RESTORE_PASSWORD.name,
                component: () => import('@/modules/restore_password/view/PasswordRestorePage.vue'),
                meta: {
                    title: 'Восстановление пароля - Буйян',
                }
            },
            {
                path: ROUTES.RESET_PASSWORD.path,
                name: ROUTES.RESET_PASSWORD.name,
                component: () => import('@/modules/restore_password/view/ResetPasswordPage.vue'),
                meta: {
                    title: 'Сброс пароля - Буйян',
                },
                props: (route) => ({
                    code: route.query.code,
                    email: route.query.email,
                })
            },
            {
                path: ROUTES.USER.path,
                name: ROUTES.USER.name,
                component: () => import('@/modules/user/views/UserPage.vue'),
                meta: {
                    title: 'Информация о пользователе - Буйян',
                }
            },
            {
                path: ROUTES.CREATE_AD.path,
                name: ROUTES.CREATE_AD.name,
                component: () => import('@/modules/create_ad/views/CreateAdPage.vue'),
                meta: {title: 'Разместить объявление - Буйян', requireConfirmedEmail: true}
            },
            {
                path: ROUTES.VACANCIES.path,
                name: ROUTES.VACANCIES.name,
                component: () => import('@/modules/vacancy/views/VacanciesPage.vue'),
                meta: {title: 'Вакансии - Буйян'}
            },
            {
                path: ROUTES.VACANCY_CREATE.path,
                name: ROUTES.VACANCY_CREATE.name,
                component: () => import('@/modules/vacancy/views/CreateOrUpdateVacancy.vue'),
                meta: {
                    title: 'Новая вакансия - Буйян',
                    requireConfirmedEmail: true
                }
            },
            {
                path: ROUTES.VACANCY_EDITOR_EDIT.path,
                name: ROUTES.VACANCY_EDITOR_EDIT.name,
                component: () => import('@/modules/vacancy/views/CreateOrUpdateVacancy.vue'),
                meta: {
                    title: 'Редактировать вакансию - Буйян',
                    requireConfirmedEmail: true
                }
            },
            {
                path: ROUTES.VACANCY_DETAIL.path,
                name: ROUTES.VACANCY_DETAIL.name,
                component: () => import('@/modules/vacancy/views/VacancyPage.vue'),
                meta: {title: 'Вакансия - Буйян'}
            },

            {
                path: ROUTES.RESUMES.path,
                name: ROUTES.RESUMES.name,
                component: () => import('@/modules/resume/views/ResumesPage.vue'),
                meta: {title: 'Резюме - Буйян'}
            },
            {
                path: ROUTES.RESUME_CREATE.path,
                name: ROUTES.RESUME_CREATE.name,
                component: () => import('@/modules/resume/views/CreateOrUpdateResume.vue'),
                meta: {
                    title: 'Новое резюме - Буйян',
                    requireConfirmedEmail: true
                }
            },
            {
                path: ROUTES.RESUME_EDITOR_EDIT.path,
                name: ROUTES.RESUME_EDITOR_EDIT.name,
                component: () => import('@/modules/resume/views/CreateOrUpdateResume.vue'),
                meta: {
                    title: 'Редактировать резюме - Буйян',
                    requireConfirmedEmail: true
                }
            },
            {
                path: ROUTES.RESUME_DETAIL.path,
                name: ROUTES.RESUME_DETAIL.name,
                component: () => import('@/modules/resume/views/ResumePage.vue'),
                meta: {title: 'Резюме - Буйян'}
            },

            {
                path: ROUTES.OFFERINGS.path,
                name: ROUTES.OFFERINGS.name,
                component: () => import('@/modules/offering/views/OfferingsPage.vue'),
                meta: {title: 'Услуги - Буйян'}
            },
            {
                path: ROUTES.OFFERING_DETAIL.path,
                name: ROUTES.OFFERING_DETAIL.name,
                component: () => import('@/modules/offering/views/OfferingPage.vue'),
                meta: {title: 'Услуга - Буйян'}
            },
            {
                path: ROUTES.OFFERING_CREATE.path,
                name: ROUTES.OFFERING_CREATE.name,
                component: () => import('@/modules/offering/views/CreateOrUpdateOffering.vue'),
                meta: {
                    title: 'Создать услугу - Буйян',
                    requireConfirmedEmail: true
                }
            },
            {
                path: ROUTES.OFFERING_EDITOR_EDIT.path,
                name: ROUTES.OFFERING_EDITOR_EDIT.name,
                component: () => import('@/modules/offering/views/CreateOrUpdateOffering.vue'),
                meta: {
                    title: 'Редактировать услугу - Буйян',
                    requireConfirmedEmail: true
                }
            },

            
            {
                path: ROUTES.PRODUCTS.path,
                name: ROUTES.PRODUCTS.name,
                component: () => import('@/modules/offering/views/OfferingsPage.vue'),
                meta: {title: 'Товары - Буйян'}
            },
            
            {
                path: ROUTES.PRODUCT_DETAIL.path,
                name: ROUTES.PRODUCT_DETAIL.name,
                component: () => import('@/modules/offering/views/OfferingPage.vue'),
                meta: {title: 'Товар - Буйян'}
            },
            
            {
                path: ROUTES.PRODUCT_CREATE.path,
                name: ROUTES.PRODUCT_CREATE.name,
                component: () => import('@/modules/offering/views/CreateOrUpdateOffering.vue'),
                meta: {
                    title: 'Создать товар - Буйян',
                    requireConfirmedEmail: true
                }
            },

            {
                path: ROUTES.PRODUCT_EDITOR_EDIT.path,
                name: ROUTES.PRODUCT_EDITOR_EDIT.name,
                component: () => import('@/modules/offering/views/CreateOrUpdateOffering.vue'),
                meta: {
                    title: 'Редактировать товар - Буйян',
                    requireConfirmedEmail: true
                }
            },
            {
                path: ROUTES.PROFILE.path,
                name: ROUTES.PROFILE.name,
                component: () => import('@/modules/profile/views/ProfileSettingsPage.vue'),
                meta: {
                    title: 'Личный кабинет - Буйян',
                    requiresAuth: true,
                }
            },
            {
                path: ROUTES.FAVORITES.path,
                name: ROUTES.FAVORITES.name,
                component: () => import('@/modules/favorites/views/FavoritesPage.vue'),
                meta: {
                    title: 'Избранное - Буйян',
                    hideSearch: true,
                }
            },
            {
                path: ROUTES.FAVORITES_FOLDER.path,
                name: ROUTES.FAVORITES_FOLDER.name,
                component: () => import('@/modules/favorites/views/FolderPage.vue'),
                meta: {
                    title: 'Папка - Буйян',
                    hideSearch: true,
                }
            },
            {
                path: ROUTES.PROFILE_SETTINGS.path,
                name: ROUTES.PROFILE_SETTINGS.name,
                component: () => import('@/modules/profile/views/ProfileSettingsPage.vue'),
                meta: {
                    title: 'Настройки - Буйян'
                }
            },
            {
                path: ROUTES.PROFILE_CHANGE_PASSWORD.path,
                name: ROUTES.PROFILE_CHANGE_PASSWORD.name,
                component: () => import('@/modules/profile/views/ChangePasswordPage.vue'),
                meta: {
                    title: 'Сменить пароль - Буйян',
                    requiresAuth: true,
                    hideSearch: true,
                }
            },
            {
                path: ROUTES.PROFILE_FAVORITES.path,
                name: ROUTES.PROFILE_FAVORITES.name,
                component: HomePage,
                meta: {
                    title: 'Профиль - Буйян'
                }
            },
            {
                path: ROUTES.PROFILE_CHAT.path,
                name: ROUTES.PROFILE_CHAT.name,
                component: () => import('@/modules/profile/views/ChatPage.vue'),
                meta: {
                    title: 'Чаты - Буйян',
                    requiresAuth: true
                }
            },
            {
                path: ROUTES.PROFILE_CHAT_DETAIL.path,
                name: ROUTES.PROFILE_CHAT_DETAIL.name,
                component: () => import('@/modules/profile/views/ChatPage.vue'),
                meta: {
                    title: 'Чат - Буйян',
                    requiresAuth: true
                }
            },
            {
                path: ROUTES.PROFILE_ORDERS.path,
                name: ROUTES.PROFILE_ORDERS.name,
                component: () => import('@/modules/offering/views/OfferingsPage.vue'),
                meta: {
                    title: 'Заказы - Буйян'
                }
            },
            {
                path: ROUTES.REFERRAL.path,
                name: ROUTES.REFERRAL.name,
                component: () => import('@/modules/profile/views/ReferralPage.vue'),
                meta: {
                    title: 'Реферальная программа - Буйян'
                }
            },
            {
                path: ROUTES.CONFIRM_EMAIL.path,
                name: ROUTES.CONFIRM_EMAIL.name,
                component: () => import('@/modules/login/components/ConfirmEmail.vue'),
                meta: {title: 'Подтверждение регистрации - Буйян'},
                props: (route) => ({token: route.query.token})
            },

            {
                path: ROUTES.MESSENGERS.path,
                name: ROUTES.MESSENGERS.name,
                component: () => import('@/modules/messenger/views/MessengersPage.vue'),
                meta: {
                    title: 'Мессенджеры - Буйян',
                    permissions: ['view_messenger']
                }
            },
            {
                path: ROUTES.MESSENGER_CREATE.path,
                name: ROUTES.MESSENGER_CREATE.name,
                component: () => import('@/modules/messenger/views/CreateOrUpdateMessenger.vue'),
                meta: {
                    title: 'Новый мессенджер - Буйян',
                    permissions: ['create_messenger']
                }
            },
            {
                path: ROUTES.MESSENGER_EDITOR_EDIT.path,
                name: ROUTES.MESSENGER_EDITOR_EDIT.name,
                component: () => import('@/modules/messenger/views/CreateOrUpdateMessenger.vue'),
                meta: {
                    title: 'Редактировать мессенджер - Буйян',
                    permissions: ['update_messenger']
                }
            },
            {
                path: ROUTES.MESSENGER_DETAIL.path,
                name: ROUTES.MESSENGER_DETAIL.name,
                component: () => import('@/modules/messenger/views/MessengerPage.vue'),
                meta: {
                    title: 'Мессенджер - Буйян',
                }
            },

            {
                path: ROUTES.REALTY.path,
                name: ROUTES.REALTY.name,
                component: () => import('@/modules/realty/views/ManyRealtyPage.vue'),
                meta: {title: 'Недвижимость - Буйян'}
            },
            {
                path: ROUTES.REALTY_DETAIL.path,
                name: ROUTES.REALTY_DETAIL.name,
                component: () => import('@/modules/realty/views/RealtyPage.vue'),
                meta: {title: 'Недвижимость - Буйян'}
            },
            {
                path: ROUTES.REALTY_CREATE.path,
                name: ROUTES.REALTY_CREATE.name,
                component: () => import('@/modules/realty/views/CreateOrUpdateRealty.vue'),
                meta: {
                    title: 'Создать недвижимость - Буйян',
                    requireConfirmedEmail: true
                }
            },
            {
                path: ROUTES.REALTY_EDITOR_EDIT.path,
                name: ROUTES.REALTY_EDITOR_EDIT.name,
                component: () => import('@/modules/realty/views/CreateOrUpdateRealty.vue'),
                meta: {
                    title: 'Редактировать недвижимость - Буйян',
                    requireConfirmedEmail: true
                }
            },
            {
                path: ROUTES.QUICK_POST_LIST.path,
                name: ROUTES.QUICK_POST_LIST.name,
                component: () => import('@/modules/quick_post/views/QuickPostListPage.vue'),
                meta: {title: 'Быстрые объявления - Буйян', hideSearch: true},
            },
            {
                path: ROUTES.QUICK_POST_CREATE.path,
                name: ROUTES.QUICK_POST_CREATE.name,
                component: () => import('@/modules/quick_post/views/QuickPostFormPage.vue'),
                meta: {title: 'Новое объявление - Буйян', requiresAuth: true, requireConfirmedEmail: true},
            },
            {
                path: ROUTES.QUICK_POST_DETAIL.path,
                name: ROUTES.QUICK_POST_DETAIL.name,
                component: () => import('@/modules/quick_post/views/QuickPostDetailPage.vue'),
                meta: {title: 'Быстрое объявление - Буйян', hideSearch: true},
            },
            {
                path: ROUTES.QUICK_POST_EDIT.path,
                name: ROUTES.QUICK_POST_EDIT.name,
                component: () => import('@/modules/quick_post/views/QuickPostFormPage.vue'),
                meta: {title: 'Редактировать объявление - Буйян', requiresAuth: true, requireConfirmedEmail: true},
            },
            {
                path: ROUTES.QUICK_POST_MY.path,
                name: ROUTES.QUICK_POST_MY.name,
                component: () => import('@/modules/quick_post/views/QuickPostMyPage.vue'),
                meta: {title: 'Мои объявления - Буйян', requiresAuth: true},
            },
            {
                path: ROUTES.PROFILE_MY_LISTINGS.path,
                name: ROUTES.PROFILE_MY_LISTINGS.name,
                component: () => import('@/modules/listing_management/views/MyListingsPage.vue'),
                meta: {title: 'Мои объявления - Буйян', requiresAuth: true},
            },
            {
                path: ROUTES.PROFILE_MY_PRODUCTS.path,
                name: ROUTES.PROFILE_MY_PRODUCTS.name,
                component: () => import('@/modules/listing_management/views/MyProductsPage.vue'),
                meta: {title: 'Мои товары - Буйян', requiresAuth: true},
            },
            {
                path: ROUTES.USER_NOTIFICATION.path,
                name: ROUTES.USER_NOTIFICATION.name,
                component: () => import('@/modules/user_notification/views/UserNotificationsPage.vue'),
                meta: {title: 'Уведомления - Буйян', requiresAuth: true}
            },
            {
                path: ROUTES.NEWS.path,
                name: ROUTES.NEWS.name,
                component: () => import('@/modules/news/views/NewsPage.vue'),
                meta: {title: 'Новости - Буйян', hideSearch: true}
            },
            {
                path: ROUTES.NEWS_DETAIL.path,
                name: ROUTES.NEWS_DETAIL.name,
                component: () => import('@/modules/news/views/NewsDetailPage.vue'),
                meta: {title: 'Новость - Буйян', hideSearch: true}
            },
            {
                path: ROUTES.RULES.path,
                name: ROUTES.RULES.name,
                component: () => import('./modules/rules/views/RulesPage.vue'),
                meta: {title: 'Правила сайта - Буйян', hideSearch: true}
            },
            {
                path: ROUTES.GROUPS.path,
                name: ROUTES.GROUPS.name,
                component: () => import('@/modules/groups/views/GroupsPage.vue'),
                meta: {title: 'Группы - Буйян', hideSearch: true}
            },
            {
                path: ROUTES.GROUPS_DETAIL.path,
                name: ROUTES.GROUPS_DETAIL.name,
                component: () => import('@/modules/groups/views/GroupDetailPage.vue'),
                meta: {title: 'Группа - Буйян', hideSearch: true}
            },
            {
                path: ROUTES.ONBOARDING.path,
                name: ROUTES.ONBOARDING.name,
                component: () => import('@/modules/onboarding/views/OnboardingPage.vue'),
                meta: {title: 'Знакомство с Буйяном', hideSearch: true, fullscreen: true}
            },
            {
                path: ROUTES.SUPPORT.path,
                name: ROUTES.SUPPORT.name,
                component: () => import('@/modules/support/views/SupportChat.vue'),
                meta: {
                    title: 'Поддержка - Буйян',
                    requiresAuth: true,
                    requireConfirmedEmail: true,
                }
            },
        ]
    }
];

const router = createRouter({
    history: createWebHistory(),
    routes
});

// Инициализируем navigation.js
setRouter(router);

// Экспортируем useNavigation для использования в компонентах
export {useNavigation} from "@/router/navigation.js";

// Создаем отдельный объект с методами навигации
const navigation = {
    /**
     * Выполняет переход по указанному маршруту
     * @param {string|{name: string, path: string}} to - Имя маршрута (из ROUTES) или объект маршрута
     * @param {Object} [params={}] - Параметры для динамических сегментов пути
     * @param {Object} [query={}] - Query-параметры URL
     * @returns {Promise<void>}
     *
     * @example
     * // Простой переход
     * navigateTo(ROUTES.HOME);
     *
     * @example
     * // С параметрами
     * navigateTo(ROUTES.VACANCY_DETAIL, { id: 123 });
     *
     * @example
     * // С query-параметрами
     * navigateTo(ROUTES.OFFERINGS, {}, { sort: 'price' });
     *
     * @example
     * // Комплексный пример
     * navigateTo({
     *   name: ROUTES.PROFILE_CHAT_DETAIL.name,
     *   params: { id: 456 },
     *   query: { message: 'hello' }
     * });
     */
    navigateTo(to, params = {}, query = {}) {
        try {
            if (!to) {
                console.warn('Маршрут не указан');
                return Promise.reject(new Error('Маршрут не указан'));
            }

            const routeObj = typeof to === 'string'
                ? {name: to, params, query}
                : {...to, params, query};

            return router.push(routeObj).catch(err => {
                if (!err.message.includes('NavigationDuplicated')) {
                    console.error('Ошибка навигации:', err);
                    throw err;
                }
            });
        } catch (error) {
            console.error('Ошибка в navigateTo:', error);
            throw error;
        }
    },

    /**
     * Генерирует URL для указанного маршрута
     * @param {string} routeName - Имя маршрута из ROUTES
     * @param {Object} [params={}] - Параметры для динамических сегментов
     * @returns {string} Абсолютный URL
     *
     * @example
     * // Получение пути вакансии
     * const path = getRoutePath(ROUTES.VACANCY_DETAIL.name, { id: 789 });
     * // в†’ "/vacancy/789"
     *
     * @example
     * // Использование в шаблоне
     * <a :href="getRoutePath(ROUTES.PROFILE.name)">Профиль</a>
     */
    getRoutePath(routeName, params = {}) {
        const route = router.resolve({name: routeName, params});
        return route.path;
    },

    getRouteParams(routeName, params = {}) {
        const route = router.resolve({name: routeName, params});
        return route.params;
    },

    /**
     * Проверка, является ли указанный маршрут активным.
     *
     * @param {string} routeName - Имя маршрута.
     * @returns {import('vue').ComputedRef<boolean>}
     *
     * @example
     * const isVacancyPage = isActiveRoute('VacancyPage');
     * if (isVacancyPage.value) { // обработка активного маршрута }
     */
    isActiveRoute(routeName) {
        return computed(() => router.currentRoute.value.name === routeName);
    },

    /**
     * Обновляет query-параметры URL без перезагрузки страницы
     * @param {Object} query - Новые query-параметры
     *
     * @example
     * // Установка параметра
     * updateQuery({ page: 2 });
     *
     * @example
     * // Удаление параметра
     * updateQuery({ page: undefined });
     *
     * @example
     * // Комплексное обновление
     * updateQuery({
     *   sort: 'date',
     *   filter: JSON.stringify({ category: 'books' })
     * });
     */
    updateQuery(query) {
        return router.replace({query: {...router.currentRoute.value.query, ...query}});
    },
    updatePushQuery(query) {
        return router.push({query: {...router.currentRoute.value.query, ...query}});
    }
};

// Добавляем методы к роутеру
router.navigateTo = navigation.navigateTo;
router.getRoutePath = navigation.getRoutePath;
router.getRouteParams = navigation.getRouteParams;
router.isActiveRoute = navigation.isActiveRoute;
router.updateQuery = navigation.updateQuery;
router.updatePushQuery = navigation.updatePushQuery;
router.ROUTES = ROUTES;

// Глобальный навигационный охранник для проверки ролей и обновления токенов
router.beforeEach(async (to, from, next) => {
    setDocumentTitle(to);
    const authStore = useAuthStore();


    // Ждем инициализации аутентификации, если она еще не завершена
    // Используем soft-continue без жесткого таймаута: если инициализация не завершилась,
    // продолжаем, но не опускаем защиту на защищенных роутов - там будет проверка isAuthenticated.
    if (!authStore.isInitialized && !router.__authInitPromise) {
        router.__authInitPromise = authStore.initAuth().finally(() => { router.__authInitPromise = null; });
        try {
            await router.__authInitPromise;
        } catch (e) {
            console.warn('Auth init error (soft-continue):', e);
        }
    }

    if (authStore.isAuthenticated || authStore.refresh_token) {
        const isAccessTokenValid = verifyJWT(authStore.access_token).valid;
        if (!isAccessTokenValid) {
            try {
                await authStore.refreshToken();
            } catch (error) {
                notifier.error('guard-session-expired', 'Ваш сеанс завершен. Пожалуйста, войдите снова.');
                return next({name: ROUTES.LOGIN.name});
            }
        } else {
        }
    }

    // Проверка доступов
    if (to.meta.permissions || to.meta.requiresAuth || to.meta.requireConfirmedEmail) {

        // Сначала проверяем авторизацию
        if (!authStore.isAuthenticated) {
            notifier.warn('guard-require-auth', 'Для выполнения этого действия необходимо войти в систему', 4000);
            return next({name: ROUTES.LOGIN.name});
        }

        // Публикация объявлений и т.п. — бэкенд блокирует это неподтверждённым/забаненным
        // (middleware.RequireConfirmedEmail). Проверяем статус из клеймов JWT здесь же,
        // чтобы не пускать в форму, которую всё равно нельзя будет отправить.
        if (to.meta.requireConfirmedEmail) {
            const verified = verifyJWT(authStore.access_token);
            const status = verified?.payload?.Status;
            const role = verified?.payload?.Role;

            if (verified?.valid && role !== ROLE_ADMIN && status !== 'active') {
                notifier.warn(
                    'guard-require-confirmed-email',
                    status === 'banned'
                        ? 'Ваш аккаунт заблокирован'
                        : 'Подтвердите почту, чтобы разместить объявление — код можно запросить в настройках профиля',
                    4500
                );
                return next({name: ROUTES.PROFILE_SETTINGS.name});
            }
        }

        // Если пользователь авторизован, загружаем права и проверяем их
        if (to.meta.permissions) {
            await authStore.fetchUserPermissions();
            const hasAccess = authStore.hasPermission(to.meta.permissions);

            if (!hasAccess) {
                notifier.error('guard-access-denied', 'У вас недостаточно прав для доступа к этому функционалу.');
                return next({name: ROUTES.HOME.name});
            }
        }

    }

    next(); // продолжаем навигацию, если доступ есть или роли не указаны
});

export default router;

